import { getAvatarProps } from '@AppModels/AvatarUtils';
import { Avatar } from '@HoudiniUI/Avatar';
import { css } from 'aphrodite';
import moment from 'moment';
import * as React from 'react';
import { getDisplayName } from '../../../../models/NameUtils';
import { Button } from '../../../../web/components-v2/Button';
import { THEME } from '../../../../web/components-v2/Theme/theme';
import { CalendarDateIcon } from '../../../../web/components/svgs/icons/CalendarDateIcon';
import { OpenNewWindowIcon } from '../../../../web/components/svgs/icons/OpenNewWindowIcon';
import { ILeadSearchView } from '../../../viewModels/leads/interfaces';
import { LeadServedSource } from '../../../viewModels/queue';
import { FollowUpDue } from './FollowUpDue';
import { OutcomePill } from '../../../components/OutcomePill';
import { RecordingActions } from './RecordingActions';
import { styleSheet } from './styles';

interface IProps {
	lead: ILeadSearchView;
	onOpen: (companyId: string) => void;
}

export const FollowUpCard: React.FC<IProps> = ({ lead, onOpen }) => {
	const companyId = lead.company?.id;
	const contact = lead.contactReferences?.[0];
	const contactEmail = contact?.primaryEmail?.value ?? contact?.emailAddresses?.[0]?.value;
	const showMeeting = !!lead.lastDeal?.isOpen && !!lead.nextMeeting;
	const appUrl = `${window.location.origin}${window.location.pathname}`;

	const companyName = lead.company?.companyName ?? 'Unknown';
	const openInNewTab = () => {
		window.open(`${appUrl}#/queue?from=${LeadServedSource.SharedLink}&companyId=${companyId}`, '_blank', 'noreferrer');
	};

	return (
		<div className={css(styleSheet.card)}>
			<div className={css(styleSheet.cardHeader)}>
				<div className={css(styleSheet.company)}>
					{companyId ? (
						<Button
							type='button'
							variant='link'
							aria-label={`Open ${companyName}`}
							styles={[styleSheet.openLead, styleSheet.cardCompanyName]}
							onClick={() => onOpen(companyId)}
						>
							{companyName}
						</Button>
					) : (
						<span className={css(styleSheet.cardCompanyName)}>{companyName}</span>
					)}
					<span className={css(styleSheet.small)}>
						{lead.company?.phoneNumbers?.[0]?.metadata?.standard ?? 'Unknown'}
					</span>
					{lead.company?.webSite ? <span className={css(styleSheet.small)}>{lead.company.webSite}</span> : null}
				</div>
				{companyId ? (
					<Button
						type='button'
						variant='ghost'
						size='icon'
						aria-label='Open in new tab'
						styles={[styleSheet.aboveOpenLead]}
						onClick={openInNewTab}
					>
						<OpenNewWindowIcon fill='currentColor' />
					</Button>
				) : null}
			</div>

			{contact ? (
				<div className={css(styleSheet.contact)}>
					<Avatar {...getAvatarProps(contact)} styles={[styleSheet.contactAvatar]} smallText={true} />
					<div className={css(styleSheet.company)}>
						<span className={css(styleSheet.contactName)}>{getDisplayName(contact)}</span>
						<span className={css(styleSheet.mutedSmall)}>
							{[contact.jobTitle, contactEmail].filter(Boolean).join(' · ')}
						</span>
					</div>
				</div>
			) : null}

			<div className={css(styleSheet.divider)} />

			<div className={css(styleSheet.cardFollowUp)}>
				<FollowUpDue followUp={lead.followUp} layout='inline' />
				{lead.followUp?.name ? <span className={css(styleSheet.cardNote)}>{lead.followUp.name}</span> : null}
				{showMeeting ? (
					<span className={css(styleSheet.meeting)}>
						<CalendarDateIcon className={css(styleSheet.icon16)} fillColor={THEME.colors.icon.primary} />
						{moment(lead.nextMeeting.startDate).format('M/DD/YY h:mm A')}
					</span>
				) : null}
			</div>

			<div className={css(styleSheet.cardFooter)}>
				<OutcomePill interaction={lead.lastInteraction} datePrefix='Last called' />
				<div className={css(styleSheet.cardFooterActions, styleSheet.aboveOpenLead)}>
					<RecordingActions phoneCall={lead.interactionPhoneCall} variant='card' />
					{lead.lastDeal && companyId ? (
						<Button asChild variant='link' size='sm'>
							<a href={`${appUrl}#/demos?companyId=${companyId}`}>Demo recordings</a>
						</Button>
					) : null}
				</div>
			</div>
		</div>
	);
};
